import { DateColDefTimeSettings, parseCellDate } from './date';
import { ColDef, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { NO_VALUE } from '../../../Ticker';
import { formatDisplayValueOrDefault } from '../../helpers/getCellValue';
import { getCorrectDateFormat } from './getCorrectDateFormat';
import { TimeFilter } from './TimeFilter';
import {
  COMPACT_DATE_TIME_FORMAT,
  DISPLAY_DATE_TIME_24H_FORMAT,
  DISPLAY_DATE_TIME_AM_PM_FORMAT,
  DISPLAY_DATE_TIME_FORMATS,
  LOCALIZED_DATE_TIME_FORMATS,
  MARKET_DATA_SOURCE_ZONE,
  PRE_FORMATTED_DATE_TIME_FORMATS,
  TIME_24H_FORMAT,
  TIME_AM_PM_FORMAT,
} from './formats';
import { compareDateFilterCell, DateFilterCellValue } from './dateFilter';

const toDisplayZone = (dt: DateTime, timezone?: string): DateTime => (timezone ? dt.setZone(timezone) : dt);

const isLocalizedDateTimeValue = (value: unknown): boolean => {
  if (value == null || value === '') return false;

  const str = String(value);
  return LOCALIZED_DATE_TIME_FORMATS.some(format => DateTime.fromFormat(str, format).isValid);
};

/** Parses combined date+time cell values (e.g. scanner lastTradeTime). */
export const parseCellDateTime = (value: unknown, timezone?: string): DateTime | null => {
  if (value == null || value === '') return null;

  const str = String(value);
  const displayZone = timezone ?? MARKET_DATA_SOURCE_ZONE;

  // Compact timestamps are Eastern source times. Parse in that zone, then
  // convert to the pro user settings timezone for display.
  // Must run before fromISO — Luxon accepts basic ISO like "20240115" and would
  // otherwise bind to the local zone and skip these branches.
  if (/^\d{14}$/.test(str)) {
    const dt = DateTime.fromFormat(str, COMPACT_DATE_TIME_FORMAT, { zone: MARKET_DATA_SOURCE_ZONE });
    return dt.isValid ? toDisplayZone(dt, timezone) : null;
  }

  const fromIso = DateTime.fromISO(str, { zone: displayZone });
  if (fromIso.isValid) return fromIso;

  for (const format of PRE_FORMATTED_DATE_TIME_FORMATS) {
    const dt = DateTime.fromFormat(str, format, { zone: displayZone });
    if (dt.isValid) return dt;
  }

  if (/^\d+$/.test(str)) {
    const num = Number(str);
    if (Number.isFinite(num) && num > 0) {
      const dt = DateTime.fromMillis(num > 1e12 ? num : num * 1000, { zone: displayZone });
      if (dt.isValid) return dt;
    }
  }

  return parseCellDate(value, timezone);
};

/**
 * Parses the already-formatted strings our valueGetters put into cells, for
 * comparators. `new Date` is unusable here: WebKit rejects the
 * AM/PM display shape, and parsing in the browser zone reorders rows whose
 * display zone crosses a DST transition.
 */
export const parseDisplayDateTime = (value: unknown): DateTime | null => {
  if (value == null || value === '') return null;

  const str = String(value);
  for (const format of DISPLAY_DATE_TIME_FORMATS) {
    const dt = DateTime.fromFormat(str, format, { zone: 'utc' });
    if (dt.isValid) return dt;
  }

  const fromIso = DateTime.fromISO(str, { zone: 'utc' });
  return fromIso.isValid ? fromIso : null;
};

/** Sorts unparseable cells (NO_VALUE, blanks) first, matching ag-grid's null handling. */
export const compareDisplayDateTime = (valueA: string, valueB: string): number => {
  const dateTimeA = parseDisplayDateTime(valueA);
  const dateTimeB = parseDisplayDateTime(valueB);

  if (!dateTimeA && !dateTimeB) return 0;
  if (!dateTimeA) return -1;
  if (!dateTimeB) return 1;

  return dateTimeA.toMillis() - dateTimeB.toMillis();
};

const getTimeValue = (time: string): number => {
  const pmAddendum = time.toUpperCase().includes('PM') ? 120000 : 0;
  return +time.replaceAll(/:|AM|PM|am|pm|\s/g, '') + pmAddendum;
};

const dateTimeComparator =
  (dateField = 'date', timeField = 'time', zone = 'utc') =>
  (_value1: string, _value2: string, node1: IRowNode, node2: IRowNode) => {
    const node1Date = node1.data[dateField];
    const node2Date = node2.data[dateField];
    const node1Time = typeof node1.data[timeField] === 'string' ? node1.data[timeField] : '';
    const node2Time = typeof node2.data[timeField] === 'string' ? node2.data[timeField] : '';

    if (!node1Date && node2Date) return -1;
    if (!node2Date && node1Date) return 1;
    if (!node1Date && !node2Date) {
      return getTimeValue(node1Time) - getTimeValue(node2Time);
    }

    const dt1 = DateTime.fromISO(`${node1Date}T${node1Time || '00:00'}`, { zone });
    const dt2 = DateTime.fromISO(`${node2Date}T${node2Time || '00:00'}`, { zone });

    return dt1.toMillis() - dt2.toMillis();
  };

/**
 * Single-field dateTime column (e.g. scanner lastTradeTime).
 * Parses source timestamps and formats date + time in the user timezone.
 */
const buildSingleDateTimeColDef = (
  colDef: ColDef,
  { timeFormat, timeOffset, timezone }: DateColDefTimeSettings,
): ColDef => ({
  cellStyle: { textAlign: 'left' },
  comparator: compareDisplayDateTime,
  filter: 'agDateColumnFilter',
  filterParams: {
    comparator: (filterLocalDateAtMidnight: Date, cellValue: DateFilterCellValue) =>
      compareDateFilterCell(filterLocalDateAtMidnight, cellValue, parseDisplayDateTime, timezone),
  },
  filterValueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
    const dateTime = parseCellDateTime(data[field], timezone);
    return dateTime?.isValid ? dateTime.toJSDate() : null;
  },
  valueFormatter: ({ value }: ValueFormatterParams): string => formatDisplayValueOrDefault(value),
  valueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
    const rawValue = data[field];
    if (rawValue == null || rawValue === '') return NO_VALUE;

    if (isLocalizedDateTimeValue(rawValue)) return String(rawValue);

    const dateTime = parseCellDateTime(rawValue, timezone);
    if (!dateTime?.isValid) return NO_VALUE;

    const displayDateTime = timezone ? dateTime : dateTime.plus({ minutes: timeOffset });
    if (timeFormat === 'AM/PM') {
      return displayDateTime.toFormat(DISPLAY_DATE_TIME_AM_PM_FORMAT);
    }
    return displayDateTime.toFormat(DISPLAY_DATE_TIME_24H_FORMAT);
  },
  ...colDef,
});

/**
 * Split date + time columns (calendars).
 */
const buildSplitDateTimeColDefs = (
  dateColDef: ColDef,
  timeColDef: ColDef,
  settings: DateColDefTimeSettings,
): [ColDef, ColDef] => {
  const timezone = settings.timezone ?? 'utc';
  return [
    {
      cellStyle: { textAlign: 'left' },
      comparator: dateTimeComparator(dateColDef.field, timeColDef.field),
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear'],
        comparator(filterDateValue: Date, cellDateValue: Date | string): number {
          const filterDt = DateTime.fromJSDate(filterDateValue, { zone: timezone }).startOf('day');

          let cellDt: DateTime;
          if (typeof cellDateValue === 'string') {
            cellDt = DateTime.fromISO(cellDateValue, { zone: timezone }).startOf('day');
          } else {
            cellDt = DateTime.fromJSDate(cellDateValue as Date, { zone: timezone }).startOf('day');
          }

          if (cellDt.hasSame(filterDt, 'day')) return 0;
          return cellDt < filterDt ? -1 : 1;
        },
      },

      valueFormatter: (params: ValueFormatterParams) => {
        const dateValue = params.value;
        if (!dateValue) return NO_VALUE;
        const format = getCorrectDateFormat(dateValue);
        const originalDate = DateTime.fromISO(dateValue, { zone: MARKET_DATA_SOURCE_ZONE });
        const date = originalDate.toFormat(format);
        return date !== 'Invalid DateTime' ? date : dateValue;
      },

      ...dateColDef,
    },
    {
      cellStyle: { textAlign: 'left' },
      comparator: dateTimeComparator(dateColDef.field, timeColDef.field),
      filter: TimeFilter,
      filterParams: {
        column: timeColDef.field,
      },
      valueFormatter: ({ value }: ValueFormatterParams): string => value ?? '-',
      valueGetter: ({ colDef: { field = 'time' }, data }: ValueGetterParams) => {
        const value = data[field];
        if (!value) return value;
        const dt = DateTime.fromISO(`2000-01-01T${value}`, { zone: MARKET_DATA_SOURCE_ZONE }).setZone(timezone);
        if (settings.timeFormat === 'AM/PM') return dt.toFormat(TIME_AM_PM_FORMAT);
        return dt.toFormat(TIME_24H_FORMAT);
      },
      ...timeColDef,
    },
  ];
};

/** Combined dateTime field (scanner lastTradeTime, etc.) */
export function DateTimeColDef(colDef: ColDef, settings: DateColDefTimeSettings): ColDef;
/** Split date + time columns (calendars) */
export function DateTimeColDef(
  dateColDef: ColDef,
  timeColDef: ColDef,
  settings: DateColDefTimeSettings,
): [ColDef, ColDef];
export function DateTimeColDef(
  dateOrColDef: ColDef,
  timeOrSettings: ColDef | DateColDefTimeSettings,
  settings?: DateColDefTimeSettings,
): ColDef | [ColDef, ColDef] {
  if (settings) {
    return buildSplitDateTimeColDefs(dateOrColDef, timeOrSettings as ColDef, settings);
  }
  return buildSingleDateTimeColDef(dateOrColDef, timeOrSettings as DateColDefTimeSettings);
}
